import { useFormik } from "formik";
import { useSelector } from "react-redux";
import { contactFormValidation } from "shared/utils/validation";
import { UserFeedbackService } from "shared/services/user";
import { toastMessage } from "shared/components/common/toast";
import { Icons } from "assets";
import classNames from "classnames";
import styles from "./style.module.scss";
import CustomInput from "shared/components/common/customInput";
import CustomButton from "shared/components/common/customButton";
import CustomTextArea from "shared/components/common/customTextArea";

const ContactForm = () => {
  const MAX_WORDS = 3000;
  const user = useSelector((state: any) => state.root.auth?.user || {});

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      fullName: user.display_name || "",
      phone: user.phone || "",
      email: user.email || "",
      message: "",
    },
    validationSchema: contactFormValidation(MAX_WORDS),
    validateOnChange: true,
    validateOnBlur: true,

    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        setSubmitting(true);

        const payload = {
          full_name: values.fullName,
          email: values.email,
          phone: values.phone,
          message: values.message,
        };

        const response = await UserFeedbackService(payload);

        if (response?.status === 200 || response?.status === 201) {
          toastMessage("success", "Your message has been sent successfully!");
          resetForm();
        } else {
          toastMessage(
            "error",
            response?.data?.message ||
              "Failed to send message. Please try again.",
          );
        }
      } catch (error: any) {
        console.error("Error submitting feedback:", error);
        toastMessage(
          "error",
          error?.response?.data?.message ||
            "Something went wrong. Please try again later.",
        );
      } finally {
        setSubmitting(false);
      }
    },
  });

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    isSubmitting,
  } = formik;

  return (
    <div className={classNames(styles.formContainer, "grid")}>
      <h1>Get In Touch</h1>
      <p>
        You can also get in touch with using the form below. Just write your
        issue and we'll try to contact you back as soon as possible.
      </p>
      <div>
        <CustomInput
          Icon={Icons.PersonIcon}
          label="Full Name"
          name="fullName"
          placeholder="e.g. First Last"
          required
          value={values.fullName}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.fullName && errors.fullName ? errors.fullName : ""}
          customInputContainer={styles.inputContainer}
        />

        <CustomInput
          Icon={Icons.Phone}
          name="phone"
          label="Phone No"
          value={values.phone}
          required={true}
          placeholder="e.g. +1 405-235-3313"
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.phone && errors.phone ? errors.phone : ""}
          customInputContainer={classNames(
            styles.inputContainer,
            styles.phoneInputContainer,
          )}
        />

        <CustomInput
          Icon={Icons.DummyEmail}
          label="Email Address"
          name="email"
          placeholder="e.g. abc@example.com"
          required
          value={values.email}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.email && errors.email ? errors.email : ""}
          customInputContainer={styles.inputContainer}
        />

        <CustomTextArea
          label="Message"
          name="message"
          placeholder="How can we assist you?"
          value={values.message}
          onChange={handleChange}
          onBlur={handleBlur}
          required
          limit={MAX_WORDS}
          showLimit
          error={touched.message && errors.message ? errors.message : ""}
          customInputContainer={styles.inputContainer}
        />
      </div>
      <CustomButton
        title="Submit Message"
        Icon={Icons.ArrowRightCircleWhite}
        IconDirection="left"
        containerStyle={classNames(styles.sendMessage)}
        onClick={() => handleSubmit()}
        loading={isSubmitting}
        disabled={isSubmitting}
      />
    </div>
  );
};

export default ContactForm;
